import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../api";
import { toast } from "sonner";
import { useAuth } from "../store";
import {
  RefreshCw, Play, Square, FileText, Bell, BellOff, Zap, Trash2,
  ShieldCheck, Download, X,
} from "lucide-react";

const SILENCE_OPTIONS = [
  { label: "1h", hours: 1 },
  { label: "8h", hours: 8 },
  { label: "24h", hours: 24 },
];

const STATE_STYLES = {
  running: "text-emerald-400 border-emerald-500/40",
  exited: "text-rose-400 border-rose-500/40",
  restarting: "text-amber-400 border-amber-500/40",
  paused: "text-amber-400 border-amber-500/40",
  unavailable: "text-muted-foreground border-border",
  missing: "text-rose-400 border-rose-500/40",
  unknown: "text-muted-foreground border-border",
};

function StatePill({ state, status }) {
  const cls = STATE_STYLES[state] || STATE_STYLES.unknown;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-[0.18em] ${cls}`}
      title={status}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${state === "running" ? "bg-emerald-500 live-dot" : state === "exited" || state === "missing" ? "bg-rose-500" : "bg-muted-foreground"}`} />
      {state}
    </span>
  );
}

export default function Operations() {
  const { admin } = useAuth();
  const isOwner = admin?.role === "owner";

  const [services, setServices] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [isProd, setIsProd] = useState(false);
  const [alertKinds, setAlertKinds] = useState([]);
  const [silences, setSilences] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({}); // {key: true}
  const [logsModal, setLogsModal] = useState(null); // {service, lines, logs}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q, k, sl] = await Promise.all([
        api.get("/admin/ops/services"),
        api.get("/admin/alerts/kinds"),
        api.get("/admin/alerts/silences"),
        Promise.resolve({ data: null }), // placeholder to keep array length stable
      ]);
      setServices(s.data.services);
      setIsProd(s.data.is_prod);
      setQuickActions(s.data.quick_actions);
      setAlertKinds(q.data);
      const map = {};
      k.data.forEach((row) => (map[row.kind] = row));
      setSilences(map);
      void sl;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15_000);
    return () => clearInterval(id);
  }, [load]);

  const withRunning = async (key, fn) => {
    setRunning((r) => ({ ...r, [key]: true }));
    try {
      await fn();
    } finally {
      setRunning((r) => ({ ...r, [key]: false }));
    }
  };

  const serviceAction = (name, action) =>
    withRunning(`svc:${name}:${action}`, async () => {
      try {
        const { data } = await api.post(`/admin/ops/services/${name}/action`, { action });
        toast.success(`${name}: ${action} ok`);
        if (data?.output) console.log(data.output);
        load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    });

  const showLogs = (name) =>
    withRunning(`svc:${name}:logs`, async () => {
      try {
        const { data } = await api.get(`/admin/ops/services/${name}/logs?lines=200`);
        setLogsModal(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    });

  const quickAction = (id, danger) =>
    withRunning(`q:${id}`, async () => {
      if (danger && !window.confirm(`Run "${id}"? This is a privileged action.`)) return;
      try {
        const { data } = await api.post(`/admin/ops/quick/${id}`);
        toast.success(`${id} done`);
        if (data?.output) console.log(data.output);
        load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    });

  const silence = async (kind, hours) => {
    try {
      await api.post(`/admin/alerts/silences/${kind}`, { hours });
      toast.success(`${kind} silenced for ${hours}h`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unsilence = async (kind) => {
    try {
      await api.delete(`/admin/alerts/silences/${kind}`);
      toast.success(`${kind} unsilenced`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-10" data-testid="operations-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Operations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Infrastructure controls + alert management.
            {!isOwner && (
              <span className="ml-2 text-amber-400">
                (read-only — owner role required for actions)
              </span>
            )}
          </p>
        </div>
        <div className="text-xs font-mono text-muted-foreground">
          {loading ? "…" : `${services.length} services · ${alertKinds.length} alert kinds · ${Object.keys(silences).length} silenced`}
        </div>
      </div>

      {!isProd && (
        <div
          className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm flex items-start gap-3"
          data-testid="dev-banner"
        >
          <Zap className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-amber-400">DEV environment</div>
            <div className="text-muted-foreground text-xs mt-1">
              Service control buttons are disabled here. Real Docker actions only run on the PROD server (clipra.pro).
              You can still preview the layout and silence alerts.
            </div>
          </div>
        </div>
      )}

      {/* ── Quick actions ──────────────────────────── */}
      <section className="space-y-3">
        <h2 className="font-display text-xl tracking-tight">Quick actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
          {quickActions.map((q) => {
            const Icon = q.id === "clean_docker" ? Trash2 : q.id === "renew_ssl" ? ShieldCheck : Download;
            const busy = running[`q:${q.id}`];
            const disabled = !isOwner || !isProd || busy;
            return (
              <div
                key={q.id}
                data-testid={`quick-${q.id}`}
                className="bg-background p-5 flex flex-col gap-3"
              >
                <div className="flex items-start gap-3">
                  <Icon className={`w-5 h-5 mt-0.5 ${q.danger ? "text-rose-400" : "text-foreground"}`} />
                  <div className="min-w-0">
                    <div className="font-display text-base">{q.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{q.what}</div>
                  </div>
                </div>
                <button
                  onClick={() => quickAction(q.id, q.danger)}
                  disabled={disabled}
                  data-testid={`quick-${q.id}-run`}
                  className={`mt-auto px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] border transition-colors ${
                    disabled
                      ? "border-border text-muted-foreground/50 cursor-not-allowed"
                      : q.danger
                      ? "border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                      : "border-border text-foreground hover:border-foreground/40 hover:bg-secondary/40"
                  }`}
                >
                  {busy ? "Running…" : `Run ${q.emoji || ""}`}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Services ───────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="font-display text-xl tracking-tight">Services</h2>
        <div className="space-y-px bg-border">
          {services.map((s) => {
            const busy = ["restart", "start", "stop", "logs"].some((a) => running[`svc:${s.name}:${a}`]);
            const isMissingOrUnavail = s.state === "unavailable" || s.state === "missing";
            const canAct = isOwner && isProd && !busy && !isMissingOrUnavail;
            return (
              <div
                key={s.name}
                data-testid={`service-${s.name}`}
                className="bg-background p-5"
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-display text-lg tracking-tight">{s.name}</span>
                    <StatePill state={s.state} status={s.status} />
                    {s.health && (
                      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                        health: {s.health}
                      </span>
                    )}
                    {s.uptime && (
                      <span className="text-[11px] font-mono text-muted-foreground">
                        up {s.uptime}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <ActionBtn
                      onClick={() => serviceAction(s.name, "restart")}
                      disabled={!canAct}
                      testid={`service-${s.name}-restart`}
                      icon={RefreshCw}
                      label="Restart"
                      busy={running[`svc:${s.name}:restart`]}
                    />
                    <ActionBtn
                      onClick={() => serviceAction(s.name, "start")}
                      disabled={!canAct || s.state === "running"}
                      testid={`service-${s.name}-start`}
                      icon={Play}
                      label="Start"
                    />
                    <ActionBtn
                      onClick={() => serviceAction(s.name, "stop")}
                      disabled={!canAct || s.state !== "running"}
                      testid={`service-${s.name}-stop`}
                      icon={Square}
                      label="Stop"
                      tone="danger"
                    />
                    <ActionBtn
                      onClick={() => showLogs(s.name)}
                      disabled={busy || s.state === "missing"}
                      testid={`service-${s.name}-logs`}
                      icon={FileText}
                      label="Logs"
                      tone="muted"
                    />
                  </div>
                </div>
                {s.image && (
                  <div className="mt-2 text-[11px] font-mono text-muted-foreground/70 truncate">
                    {s.image}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Alerts ─────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="font-display text-xl tracking-tight">Alerts</h2>
        <div className="space-y-px bg-border">
          {alertKinds.map((k) => {
            const sil = silences[k.kind];
            const isSilenced = !!sil;
            return (
              <div
                key={k.kind}
                data-testid={`alert-kind-${k.kind}`}
                className={`bg-background p-5 ${isSilenced ? "opacity-70" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="text-2xl leading-none">{k.emoji}</span>
                    <div className="min-w-0">
                      <div className="font-display text-lg flex items-center gap-2">
                        {k.label}
                        {isSilenced && (
                          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-400 border border-amber-500/40 px-1">
                            silenced · until {new Date(sil.until).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">{k.what}</div>
                      {k.actions?.length > 0 && (
                        <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                          {k.actions.map((a, i) => (
                            <li
                              key={i}
                              className="flex gap-2"
                              dangerouslySetInnerHTML={{ __html: `<span class="text-foreground/40">→</span> ${a}` }}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    {isSilenced ? (
                      <button
                        onClick={() => unsilence(k.kind)}
                        data-testid={`alert-unsilence-${k.kind}`}
                        className="px-3 py-1.5 text-xs font-mono uppercase border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors flex items-center gap-1.5"
                      >
                        <Bell className="w-3 h-3" /> Unsilence
                      </button>
                    ) : (
                      SILENCE_OPTIONS.map((o) => (
                        <button
                          key={o.hours}
                          onClick={() => silence(k.kind, o.hours)}
                          data-testid={`alert-silence-${k.kind}-${o.hours}`}
                          className="px-3 py-1.5 text-xs font-mono uppercase border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors flex items-center gap-1.5"
                        >
                          <BellOff className="w-3 h-3" /> {o.label}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Logs modal ─────────────────────────────── */}
      {logsModal && (
        <div
          className="fixed inset-0 bg-background/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setLogsModal(null)}
          data-testid="logs-modal"
        >
          <div
            className="bg-background border border-border w-full max-w-5xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="font-display text-base">
                Logs · {logsModal.service}
                <span className="ml-2 text-xs font-mono text-muted-foreground">
                  last {logsModal.lines}
                </span>
              </div>
              <button
                onClick={() => setLogsModal(null)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="logs-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-5 text-[11px] font-mono whitespace-pre-wrap text-muted-foreground">
              {logsModal.logs || "(no output)"}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ onClick, disabled, testid, icon: Icon, label, busy, tone }) {
  const base =
    "px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.16em] border flex items-center gap-1.5 transition-colors";
  const enabled =
    tone === "danger"
      ? "border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
      : tone === "muted"
      ? "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
      : "border-border text-foreground hover:border-foreground/40 hover:bg-secondary/40";
  const off = "border-border text-muted-foreground/40 cursor-not-allowed";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`${base} ${disabled ? off : enabled}`}
    >
      <Icon className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
      {label}
    </button>
  );
}
